import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { GraduationCap, Loader2, ShoppingBag } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { StarRating } from "@/components/StarRating";
import ProductReviewsDialog from "@/components/ProductReviewsDialog";
import { TransferPurchasePanel } from "@/components/marketplace/TransferPurchasePanel";
import { XpfPrice } from "@/components/XpfPrice";
import { TamaPrice } from "@/components/TamaPrice";
import { useCheckoutRouting } from "@/hooks/useCheckoutRouting";
import { fetchProductContact, type ProductContact } from "@/services/marketplace/productContacts";

interface Product {
  id: string;
  title: string;
  description: string | null;
  price: number;
  type: string;
  user_id: string;
  thumbnail_url?: string | null;
  is_tama_catalog?: boolean;
  service_type?: string | null;
  service_delivery_method?: string | null;
  service_duration?: string | null;
  availability?: string | null;
  profiles: {
    id: string;
    full_name: string | null;
    username: string | null;
    avatar_url: string | null;
  };
}

interface ProductPurchaseProps {
  product: Product;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Mode lecture seule : le vendeur consulte sa propre fiche, aucun paiement proposé. */
  readOnly?: boolean;
}

export const ProductPurchase = ({ product, open, onClose, onSuccess, readOnly = false }: ProductPurchaseProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading] = useState(false);
  const isTama = product.is_tama_catalog === true;
  const [buyingTama, setBuyingTama] = useState(false);

  /** Catalogue TAMA : achat en Coco via l'Edge Function dédiée (jamais de virement Pro). */
  const buyTamaProduct = async () => {
    setBuyingTama(true);
    try {
      const { data, error } = await supabase.functions.invoke("purchase-tama-product", {
        body: { product_id: product.id },
      });
      const message = (data as { error?: string })?.error;
      if (error || message) throw new Error(message ?? "Achat impossible");

      toast({ title: "Achat confirmé", description: `${product.title} est désormais dans vos achats.` });
      setHasPurchased(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Achat impossible",
        description: err instanceof Error ? err.message : "Veuillez réessayer plus tard",
      });
    } finally {
      setBuyingTama(false);
    }
  };

  const [hasPurchased, setHasPurchased] = useState(false);
  const [productRating, setProductRating] = useState({ average_rating: 0, total_reviews: 0 });
  // Lecture seule : la popup n'expose jamais de formulaire d'avis.
  const [showReviews, setShowReviews] = useState(false);
  const { routing } = useCheckoutRouting();
  // Coordonnées du vendeur : lisibles seulement par lui, ses acheteurs et les admins.
  const [contact, setContact] = useState<ProductContact>({ contactEmail: null, contactPhone: null });

  useEffect(() => {
    if (open && user) {
      checkPurchaseStatus();
      fetchProductRating();
      fetchProductContact(product.id).then(setContact);
    }
  }, [open, user]);

  // L'écriture d'un avis se fait uniquement depuis « Mes achats et commandes ».
  const checkPurchaseStatus = async () => {
    if (!user) return;

    const { data } = await supabase
      .from("purchases")
      .select("id")
      .eq("buyer_id", user.id)
      .eq("product_id", product.id)
      .single();

    setHasPurchased(!!data);
  };

  const fetchProductRating = async () => {
    const { data } = await supabase.rpc("get_product_rating" as any, {
      p_product_id: product.id,
    });

    if (data && Array.isArray(data) && data.length > 0) {
      setProductRating(data[0]);
    }
  };

  const getTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      digital_content: "Contenu digital",
      digital_product: "Produit digital",
      service: "Service",
      physical: "Produit physique",
    };
    return types[type] || type;
  };

  const getServiceTypeLabel = (serviceType: string) => {
    const types: Record<string, string> = {
      coaching_conseil: "Coaching & Conseil",
      prestation_digitale: "Prestation digitale",
      cours_formations: "Cours & Formations",
    };
    return types[serviceType] || serviceType;
  };

  const getDeliveryMethodLabel = (method: string) => {
    const methods: Record<string, string> = {
      en_ligne: "En ligne",
      sur_place: "Sur place",
      hybride: "Hybride",
    };
    return methods[method] || method;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="flex-shrink-0 p-6 pb-4">
          <DialogTitle className="text-2xl">Fiche produit</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-4">
          {/* Product image */}
          {product.thumbnail_url && (
            <div className="aspect-video bg-muted rounded-lg overflow-hidden">
              <img 
                src={product.thumbnail_url} 
                alt={product.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Product details */}
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-bold mb-2">{product.title}</h2>
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  {getTypeLabel(product.type)}
                </div>
                {productRating.total_reviews > 0 && (
                  <button
                    type="button"
                    aria-label="Voir les avis"
                    className="flex items-center gap-2 hover:opacity-80"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowReviews(true);
                    }}
                  >
                    <StarRating rating={productRating.average_rating} size="sm" showValue />
                    <span className="text-sm text-muted-foreground">
                      ({productRating.total_reviews} avis)
                    </span>
                  </button>
                )}
              </div>
            </div>

            {product.description && (
              <div>
                <h3 className="font-semibold mb-2">Description</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{product.description}</p>
              </div>
            )}

            {/* Service-specific information */}
            {product.type === 'service' && (
              <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
                <h3 className="font-semibold">Détails du service</h3>
                
                {product.service_type && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Type :</span>
                    <span className="text-sm font-medium">{getServiceTypeLabel(product.service_type)}</span>
                  </div>
                )}

                {product.service_delivery_method && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Mode de livraison :</span>
                    <span className="text-sm font-medium">{getDeliveryMethodLabel(product.service_delivery_method)}</span>
                  </div>
                )}

                {product.service_duration && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Durée estimée :</span>
                    <span className="text-sm font-medium">{product.service_duration}</span>
                  </div>
                )}

                {contact.contactPhone && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Contact :</span>
                    <span className="text-sm font-medium">{contact.contactPhone}</span>
                  </div>
                )}

                {contact.contactEmail && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Email :</span>
                    <span className="text-sm font-medium break-all">{contact.contactEmail}</span>
                  </div>
                )}

                {product.availability && (
                  <div>
                    <span className="text-sm text-muted-foreground">Disponibilité :</span>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{product.availability}</p>
                  </div>
                )}
              </div>
            )}

            {/* Vendeur */}
            <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
              {isTama ? (
                <>
                  <GraduationCap className="h-10 w-10 text-primary" aria-hidden="true" />
                  <div>
                    <p className="font-medium">Vendu par TAMA</p>
                    <p className="text-sm text-muted-foreground">Catalogue officiel de la plateforme</p>
                  </div>
                </>
              ) : (
                <>
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={product.profiles.avatar_url || undefined} />
                    <AvatarFallback>
                      {product.profiles.username?.[0]?.toUpperCase() || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">Vendu par un Pro vérifié</p>
                    <p className="text-sm text-muted-foreground">
                      {product.profiles.username || product.profiles.full_name || "Anonyme"}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Prix : Coco pour TAMA, francs + euro pour les vendeurs Pro. */}
            <div className="flex items-center justify-between gap-3 p-4 border-2 border-primary rounded-lg">
              <span className="text-lg font-semibold">Prix :</span>
              {isTama ? (
                <TamaPrice cocoAmount={product.price} className="items-end text-right" />
              ) : (
                <XpfPrice amountXpf={product.price} />
              )}
            </div>

            {readOnly ? (
              /* Le vendeur consulte sa propre fiche : aucun rail de paiement. */
              <p className="text-sm text-muted-foreground p-4 rounded-lg bg-muted">
                Ceci est votre produit : fiche en lecture seule.
              </p>
            ) : isTama ? (
              /* Catalogue TAMA : paiement en Coco, jamais de virement Pro. */
              hasPurchased ? (
                <p className="text-sm text-muted-foreground p-4 rounded-lg bg-muted">
                  Vous possédez déjà ce contenu : retrouvez-le dans vos achats.
                </p>
              ) : (
                <Button className="w-full gap-2" disabled={buyingTama || !user} onClick={buyTamaProduct}>
                  {buyingTama ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <ShoppingBag className="h-4 w-4" aria-hidden="true" />
                  )}
                  Acheter en Coco
                </Button>
              )
            ) : routing.proCatalogPay === "transfer" ? (
              /* Route Pro : règlement par virement direct au vendeur. */
              <TransferPurchasePanel
                productId={product.id}
                priceXpf={product.price}
                sellerName={product.profiles.username || product.profiles.full_name || "le vendeur"}
              />
            ) : (
              <p className="text-sm text-muted-foreground p-4 rounded-lg bg-muted">
                Les achats auprès des vendeurs Pro sont momentanément suspendus.
              </p>
            )}

          </div>
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 border-t p-4 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>

      {showReviews && (
        <ProductReviewsDialog
          productId={product.id}
          productTitle={product.title}
          open={showReviews}
          onClose={() => setShowReviews(false)}
        />
      )}
    </Dialog>

  );
};

export default ProductPurchase;
